import {
  IServiceRequest,
  TUseServiceRequestsOptions,
} from '@commenda/types/src/service-request/types';
import { AppType, SWRFetcher, useApp } from '@commenda/utils';
import { useMemo } from 'react';
import useSWR from 'swr';
import { API_BASE_PATH, SERVICE_REQUESTS_SERVER_PAGE_LIMIT } from './constants';

const maxPageSize = SERVICE_REQUESTS_SERVER_PAGE_LIMIT;

export interface IServiceRequestResponse {
  items: IServiceRequest[];
  nextCursor?: string;
  meta: {
    filteredTotal: number;
    totalNonArchived: number;
  };
}

type HookOptions = TUseServiceRequestsOptions & {
  cursor?: string;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isAgentApp?: boolean;
};

export const useServiceRequestsPaginated = (options?: HookOptions) => {
  const { appType } = useApp();
  const isAgentApp = appType === AppType.AGENT;

  const key = buildPaginatedQueryString({ ...options, isAgentApp });

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<IServiceRequestResponse>(
      key,
      key
        ? SWRFetcher(
            {
              method: 'GET',
            },
            undefined
          )
        : null
    );

  const items = useMemo<IServiceRequest[]>(() => {
    if (!data) return [];
    return (data as IServiceRequestResponse)?.items ?? [];
  }, [data]);

  const nextCursor = data?.nextCursor;

  const meta = data?.meta;

  return {
    data: items,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
    nextCursor,
    meta,
  };
};

export function buildPaginatedQueryString(
  options: HookOptions | undefined,
  route = '/pagination'
): string | null {
  const {
    search,
    corporationId,
    companyId,
    companyIds,
    serviceRequestId,
    excludedServiceTypes,
    includeServiceTypes,
    isCommendaFiling,
    serviceCategories,
    serviceSubcategories,
    serviceCompositeIds,
    excludeClientManagedFilings,
    assignedUserId,
    assignedAgentFirmUserId,
    assignedAgentFirmId,
    includedStatuses,
    enabled = true,
    cursor,
    pageSize = maxPageSize,
    assigned,
    isAgentFirmAssigned,
    // Sales Tax Registration filters
    salesTaxRegistrationStates,
    // Sales Tax Filing filters
    salesTaxFilingStates,
    dueDateStart,
    dueDateEnd,
    salesTaxFilingStatuses,
    salesTaxFilingFrequencies,
    salesTaxFilingReportingPeriodStart,
    salesTaxFilingReportingPeriodEnd,
    complianceTypes,
    serviceRequestIds,
    extends: extendFields,
    incorporationCountries,
    sortBy,
    sortOrder,
    isAgentApp,
  } = options ?? {};

  if (!enabled) return null;

  const q = new URLSearchParams();

  if (pageSize) q.set('paginationConfig[limit]', String(pageSize));
  if (cursor) q.set('paginationConfig[cursor]', cursor);
  if (sortBy) q.set('paginationConfig[sortBy]', sortBy);
  if (sortOrder) q.set('paginationConfig[sortOrder]', sortOrder);

  if (search) q.append('filterConfig[search]', search);
  if (corporationId)
    q.append('filterConfig[corporationId]', String(corporationId));
  if (companyId) q.append('filterConfig[companyId]', String(companyId));
  companyIds?.forEach((id) =>
    q.append('filterConfig[companyIds][]', String(id))
  );
  if (serviceRequestId)
    q.append('filterConfig[serviceRequestId]', String(serviceRequestId));
  if (assignedUserId)
    q.append('filterConfig[assignedUserId]', String(assignedUserId));
  if (assignedAgentFirmUserId)
    q.append(
      'filterConfig[assignedAgentFirmUserId]',
      String(assignedAgentFirmUserId)
    );
  if (assignedAgentFirmId)
    q.append('filterConfig[assignedAgentFirmId]', String(assignedAgentFirmId));
  if (assigned !== undefined)
    q.append('filterConfig[assigned]', String(assigned));
  if (isAgentFirmAssigned !== undefined)
    q.append('filterConfig[isAgentFirmAssigned]', String(isAgentFirmAssigned));

  includeServiceTypes?.forEach((type) =>
    q.append('filterConfig[includeServiceTypes][]', String(type))
  );
  excludedServiceTypes?.forEach((type) =>
    q.append('filterConfig[excludedServiceTypes][]', String(type))
  );
  if (isCommendaFiling !== undefined)
    q.append('filterConfig[isCommendaFiling]', String(isCommendaFiling));
  serviceCategories?.forEach((category) =>
    q.append('filterConfig[serviceCategories][]', category)
  );
  serviceSubcategories?.forEach((subcategory) =>
    q.append('filterConfig[serviceSubcategories][]', subcategory)
  );
  serviceCompositeIds?.forEach((id) =>
    q.append('filterConfig[serviceCompositeIds][]', id)
  );
  if (excludeClientManagedFilings !== undefined)
    q.append(
      'filterConfig[excludeClientManagedFilings]',
      String(excludeClientManagedFilings)
    );
  includedStatuses?.forEach((s) =>
    q.append('filterConfig[includedStatuses][]', String(s))
  );

  // Sales Tax Registration filters
  salesTaxRegistrationStates?.forEach((state) =>
    q.append('filterConfig[salesTaxRegistrationStates][]', state)
  );

  // Sales Tax Filing filters
  salesTaxFilingStates?.forEach((state) =>
    q.append('filterConfig[salesTaxFilingStates][]', state)
  );
  if (dueDateStart) q.append('filterConfig[dueDateStart]', dueDateStart);
  if (dueDateEnd) q.append('filterConfig[dueDateEnd]', dueDateEnd);
  salesTaxFilingStatuses?.forEach((status) =>
    q.append('filterConfig[salesTaxFilingStatuses][]', status)
  );
  salesTaxFilingFrequencies?.forEach((freq) =>
    q.append('filterConfig[salesTaxFilingFrequencies][]', freq)
  );
  if (salesTaxFilingReportingPeriodStart)
    q.append(
      'filterConfig[salesTaxFilingReportingPeriodStart]',
      salesTaxFilingReportingPeriodStart
    );
  if (salesTaxFilingReportingPeriodEnd)
    q.append(
      'filterConfig[salesTaxFilingReportingPeriodEnd]',
      salesTaxFilingReportingPeriodEnd
    );

  complianceTypes?.forEach((typeId) =>
    q.append('filterConfig[complianceTypes][]', typeId)
  );

  if (serviceRequestIds?.length) {
    q.set('filterConfig[serviceRequestIds]', serviceRequestIds.join(','));
  }

  extendFields?.forEach((field) => q.append('extends[]', field));

  incorporationCountries?.forEach((country) =>
    q.append('filterConfig[incorporationCountries][]', country)
  );

  if (isAgentApp) q.set('isAgentApp', 'true');

  const endpoint = `${API_BASE_PATH}${route}`;
  const qs = q.toString();
  return qs ? `${endpoint}?${qs}` : endpoint;
}
